import React from 'react';
import { useTranslation } from 'react-i18next';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../i18n/constants/keys';
import {
  Button,

  FloatingLabelInput,
  FloatingLabelSelect,
} from '../../../../../../ui';
import { CustomDatePicker } from '../../../../../../ui/components/Common/TextFields/CustomDatePicker';
import { CustomTimePicker } from '../../../../../../ui/components/Common/TextFields/CustomTimePicker';
import { useConfigurations } from './Hooks';

interface IConfiguration {
  interviewId: string;
  onRoundCreated: () => void;
  jobOpeningId: string;
}

export const Configurations = ({
  interviewId,
  onRoundCreated,
  jobOpeningId,
}: IConfiguration) => {
  const { t } = useTranslation(NS.adminPages);
  const SI = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews;
  const {
    title,
    setTitle,
    defaultEmployeeUserOptions,
    handleHostChange,
    loadOptionsDebounced,
    handleDateSelection,
    handleTimeSlotSelection,
    handlePanelists,
    selectedDates,
    selectedTimes,
    host,
    panelists,
    interviewData,
    resetForm,
    isChanged,
    handleInputChange,
    isFormValid,
    errors,
    validateForm,
    disabledSlots,
    buttonHandler,
    touched,
    handleBlur,
  } = useConfigurations({
    interviewId,
    setInterviewId: () => {},
    onRoundCreated,
  });

  return (
    <>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-5 w-full'>
        <div className='relative flex flex-col gap-5'>
          <FloatingLabelInput
              label={t(SI.configurations.roundTitle)}
              value={title}
              placeholder={t(SI.configurations.roundTitlePlaceholder)}
              type='text'
              onChange={(e) => {
                setTitle(e.target.value);
                handleInputChange('title');
              }}
              error={touched.title && errors.title}
              helperText={
                touched.title && errors.title ? t(SI.configurations.errors.title) : ''
              }
              onBlur={() => handleBlur('title')}
          />

          <FloatingLabelSelect
              label={t(SI.configurations.host)}
              required
              isMulti={false}
              value={host}
              onChange={(v) => {
                handleHostChange(v!);
                handleInputChange('host');
              }}
              loadOptions={loadOptionsDebounced}
              defaultOptions={defaultEmployeeUserOptions}
              error={touched.host && errors.host}
              helperText={touched.host && errors.host ? t(SI.configurations.errors.host) : ''}
              onBlur={() => handleBlur('host')}
          />

          <FloatingLabelSelect
              label={t(SI.configurations.panelists)}
              required
              isMulti
              value={panelists}
              onChange={(v) => {
                handlePanelists(v as any[]);
                handleInputChange('panelists');
              }}
              loadOptions={loadOptionsDebounced}
              defaultOptions={defaultEmployeeUserOptions}
              error={touched.panelists && errors.panelists}
              helperText={
                touched.panelists && errors.panelists
                  ? t(SI.configurations.errors.panelists)
                  : ''
              }
              onBlur={() => handleBlur('panelists')}
          />

          <CustomDatePicker
              onSelectDates={(d) => {
                handleDateSelection(d);
                handleInputChange('selectedDates');
              }}
              selectedDates={selectedDates}
              error={touched.selectedDates && errors.selectedDates}
              helperText={
                touched.selectedDates && errors.selectedDates
                  ? 'Please select at least one date.'
                  : undefined
              }
              onBlur={() => handleBlur('selectedDates')}
          />
        </div>

        <CustomTimePicker
            selectedDates={selectedDates}
            selectedTimes={selectedTimes}
            onTimeSelect={(d, t) => {
              handleTimeSlotSelection(d, t);
              handleInputChange('selectedTimes');
            }}
            error={touched.selectedTimes && errors.selectedTimes}
            helperText={
              touched.selectedTimes && errors.selectedTimes
                ? 'Please select at least one time slot.'
                : ''
            }
            disabledSlots={disabledSlots}
        />
      </div>

      <div className='flex justify-end gap-5 w-full border-t mt-5 py-5'>
        <Button label={t(SI.configurations.cancel)} variant='secondary' onClick={resetForm} />
        <Button
            label={
              interviewData ? t(SI.configurations.updateRound) : t(SI.configurations.createRound)
            }
            onClick={buttonHandler}
            variant='primary'
            disabled={!isChanged || !isFormValid}
        />
      </div>
    </>
  );
};
